/*
  Dancing Queens — colour groups (replaces the Swatch King app, 2026-10-23).
  Markup in snippets/dq-colour-groups.liquid. Listeners sit on the document, so
  cards added later by filtering or infinite scroll work without setup.

  - Product page: pointing at a dot shows its colour name next to the label;
    leaving the row brings back the colour of the page.
  - Product card: pointing at a tile shows that product's photos and price in
    the card, leaving the row brings the card's own back. The arrows slide the
    row when it is wider than the card.
  - Size and heel must be picked before the cart button works; another colour
    keeps the picked size (see below).
*/
(() => {
  if (window.dqColourGroups) return;
  window.dqColourGroups = true;

  const cardImages = (row) => {
    const card = row.closest('.product-card');
    if (!card) return [];
    return [card.querySelector('.product-first-img'), card.querySelector('.product-second-img')];
  };

  const swap = (img, url) => {
    if (!img || !url) return;
    if (!('dqSrc' in img.dataset)) {
      img.dataset.dqSrc = img.getAttribute('src') || '';
      img.dataset.dqSrcset = img.getAttribute('srcset') || '';
    }
    img.removeAttribute('srcset');
    img.src = url;
  };

  const restore = (img) => {
    if (!img || !('dqSrc' in img.dataset)) return;
    img.src = img.dataset.dqSrc;
    if (img.dataset.dqSrcset) img.setAttribute('srcset', img.dataset.dqSrcset);
    delete img.dataset.dqSrc;
    delete img.dataset.dqSrcset;
  };

  const cardPrice = (row) => row.closest('.product-card')?.querySelector('.product-pricing');

  const swapPrice = (pricing, tile) => {
    if (!pricing || !tile.dataset.dqPrice) return;
    if (!('dqHtml' in pricing.dataset)) {
      pricing.dataset.dqHtml = pricing.innerHTML;
      pricing.dataset.dqDiscount = pricing.classList.contains('discount-price');
    }
    const compare = tile.dataset.dqCompare;
    pricing.innerHTML =
      `<span class="product-actual-price">${tile.dataset.dqPrice}</span>` +
      (compare ? `<span class="product-compare-price">${compare}</span>` : '');
    pricing.classList.toggle('discount-price', Boolean(compare));
  };

  const restorePrice = (pricing) => {
    if (!pricing || !('dqHtml' in pricing.dataset)) return;
    pricing.innerHTML = pricing.dataset.dqHtml;
    pricing.classList.toggle('discount-price', pricing.dataset.dqDiscount === 'true');
    delete pricing.dataset.dqHtml;
    delete pricing.dataset.dqDiscount;
  };

  const show = (event) => {
    const tile = event.target.closest?.('[data-dq-colour]');
    const row = event.target.closest?.('[data-dq-swatches]');
    if (!row) return;

    const label = row.querySelector('[data-dq-colour-current]');
    if (label) label.textContent = tile ? tile.dataset.dqColour : label.dataset.dqColourCurrent;

    if (tile && tile.dataset.dqImage) {
      const [first, second] = cardImages(row);
      swap(first, tile.dataset.dqImage);
      swap(second, tile.dataset.dqImage2 || tile.dataset.dqImage);
    }
    if (tile) swapPrice(cardPrice(row), tile);
  };

  const reset = (row) => {
    const label = row.querySelector('[data-dq-colour-current]');
    if (label) label.textContent = label.dataset.dqColourCurrent;
    cardImages(row).forEach(restore);
    restorePrice(cardPrice(row));
  };

  document.addEventListener('pointerover', show);
  document.addEventListener('focusin', show);
  document.addEventListener('pointerout', (event) => {
    const row = event.target.closest?.('[data-dq-swatches]');
    if (row && !row.contains(event.relatedTarget)) reset(row);
  });
  document.addEventListener('focusout', (event) => {
    const row = event.target.closest?.('[data-dq-swatches]');
    if (row && !row.contains(event.relatedTarget)) reset(row);
  });

  // Card slider arrows: shown only when the row overflows.
  const updateArrows = (row) => {
    const track = row.querySelector('[data-dq-swatches-track]');
    const prev = row.querySelector('[data-dq-swatches-prev]');
    const next = row.querySelector('[data-dq-swatches-next]');
    if (!track || !prev || !next) return;
    const overflow = track.scrollWidth > track.clientWidth + 1;
    prev.hidden = next.hidden = !overflow;
    prev.disabled = track.scrollLeft <= 0;
    next.disabled = track.scrollLeft + track.clientWidth >= track.scrollWidth - 1;
  };

  // A card without colours keeps the colour row's space only when another card
  // on the same line has one, so names line up without empty gaps elsewhere.
  // Names take the room of the longest name on their line, so prices line up
  // also where a name runs to three lines (narrow phone cards).
  const updateReserve = () => {
    const lines = new Map();
    document.querySelectorAll('.product-card').forEach((card) => {
      if (!card.offsetParent || !card.querySelector('.pro_grid-view-item')) return;
      const top = Math.round(card.getBoundingClientRect().top + window.scrollY);
      if (!lines.has(top)) lines.set(top, []);
      lines.get(top).push(card);
    });
    lines.forEach((cards) => {
      const hasRow = cards.some((card) => card.querySelector('.dq-swatches--card'));
      cards.forEach((card) => {
        const reserve = hasRow && !card.querySelector('.dq-swatches--card');
        if (card.classList.contains('dq-card-reserve') !== reserve) card.classList.toggle('dq-card-reserve', reserve);
      });

      // The name's own height, without the room it has been given.
      const textHeight = (title) => {
        const room = title.style.minHeight;
        title.style.minHeight = '';
        const height = Math.ceil(title.getBoundingClientRect().height);
        title.style.minHeight = room;
        return height;
      };
      const titles = cards.map((card) => card.querySelector('.pro_grid-view-item .product-title')).filter(Boolean);
      if (!titles.length) return;
      // At least two lines, as in assets/dq-colour-groups.css.
      const twoLines = Math.ceil(parseFloat(getComputedStyle(titles[0]).lineHeight) * 2);
      const tallest = Math.max(twoLines, ...titles.map(textHeight));
      titles.forEach((title) => {
        const room = `${tallest}px`;
        if (title.style.minHeight !== room) title.style.minHeight = room;
      });
    });
  };

  let pending = false;
  const updateAll = () => {
    if (pending) return;
    pending = true;
    setTimeout(() => {
      pending = false;
      document.querySelectorAll('.dq-swatches--card').forEach(updateArrows);
      updateReserve();
      setupAllPicks();
    }, 50);
  };

  document.addEventListener('click', (event) => {
    const button = event.target.closest?.('[data-dq-swatches-prev], [data-dq-swatches-next]');
    if (!button) return;
    event.preventDefault();
    const row = button.closest('[data-dq-swatches]');
    const track = row.querySelector('[data-dq-swatches-track]');
    const step = (track.querySelector('.dq-swatches__tile')?.offsetWidth || 38) * 2 + 16;
    track.scrollBy({ left: button.hasAttribute('data-dq-swatches-prev') ? -step : step });
  });

  document.addEventListener(
    'scroll',
    (event) => {
      const track = event.target;
      if (track instanceof Element && track.matches('[data-dq-swatches-track]')) {
        updateArrows(track.closest('[data-dq-swatches]'));
      }
    },
    true
  );

  // Size and heel height, as Swatch King had them: nothing is chosen when the
  // page opens, the cart button stays locked and asks for the size until it
  // is picked, sold-out values leave the list (CSS). A page opened with
  // ?variant= (size kept from another colour, a shared link) counts as chosen.
  const PICK = /gr(ö|oe)sse|size|taille|taglia|talla|absatz|heel|talon|tacco|tac[oó]n/i;
  const HEEL = /absatz|heel|talon|tacco|tac[oó]n/i;
  const PROMPTS = {
    de: ['Wähle deine Grösse', 'Wähle deine Absatzhöhe'],
    en: ['Choose your size', 'Choose your heel height'],
    fr: ['Choisis ta taille', 'Choisis ta hauteur de talon'],
    it: ['Scegli la tua taglia', "Scegli l'altezza del tacco"],
    es: ['Elige tu talla', 'Elige la altura del tacón'],
  };
  const lang = (document.documentElement.lang || 'de').slice(0, 2).toLowerCase();
  const openedWithVariant = new URLSearchParams(window.location.search).has('variant');

  const optionName = (wrapper) => wrapper.querySelector('.product-variants-title')?.textContent.trim() || '';
  const prompt = (wrapper) => (PROMPTS[lang] || PROMPTS.de)[HEEL.test(optionName(wrapper)) ? 1 : 0];
  const inQuickView = (container) => Boolean(container.closest('[data-quickview-content], #quickview-side-drawer'));

  const cartButtons = (container) => {
    const buttons = [...container.querySelectorAll('[data-addtocart-wrapper]')];
    const section = container.closest('.shopify-section');
    if (section && !inQuickView(container)) buttons.push(...section.querySelectorAll('[data-sticky-atc-wrapper]'));
    return buttons;
  };

  const applyPick = (container) => {
    const open = container.querySelector('.dq-unpicked');
    container.querySelectorAll('.dq-unpicked').forEach((wrapper) => {
      const text = wrapper.querySelector('.custom-select-text');
      if (text && text.textContent.trim() !== prompt(wrapper)) text.innerHTML = `<strong>${prompt(wrapper)}</strong>`;
    });
    container.toggleAttribute('data-dq-locked', Boolean(open));
    // Unlocking needs nothing here: the pick's change event has just let the
    // theme set the button for the chosen variant.
    cartButtons(container).forEach((button) => {
      button.classList.toggle('dq-locked', Boolean(open));
      if (!open) return;
      button.setAttribute('disabled', 'true');
      const text = button.querySelector('[data-addtocart-text]');
      if (text) text.textContent = prompt(open);
    });
  };

  const setupPick = (container) => {
    if (container.hasAttribute('data-dq-pick')) return;
    const wrappers = [...container.querySelectorAll('[data-product-variants] .product-variants-wrapper')].filter(
      (wrapper) => PICK.test(optionName(wrapper)) && wrapper.querySelector('[data-custom-select]')
    );
    if (!wrappers.length) return;
    container.setAttribute('data-dq-pick', '');
    wrappers.forEach((wrapper) => wrapper.classList.add('dq-pick'));

    const json = container.querySelector('[type="application/json"][data-name="product-variants"]');
    const variants = json ? JSON.parse(json.textContent) : [];
    if (!variants.some((variant) => variant.available)) return;
    let open = null;
    if (openedWithVariant && !inQuickView(container)) {
      try {
        open = JSON.parse(sessionStorage.getItem('dqUnpicked') || '[]');
        sessionStorage.removeItem('dqUnpicked');
      } catch (error) {
        open = [];
      }
    }

    wrappers.forEach((wrapper) => {
      if (open && !open.includes(optionName(wrapper).toLowerCase())) return;
      if (wrapper.querySelectorAll('.custom-select-item').length > 1) wrapper.classList.add('dq-unpicked');
    });
    applyPick(container);
  };

  // A pick: the theme sets the value on the label's click. Choosing the value
  // that was already checked fires no change, so one is sent for the theme.
  document.addEventListener('click', (event) => {
    const label = event.target.closest?.('.dq-unpicked .custom-select-item label.option');
    if (!label) return;
    const wrapper = label.closest('.dq-unpicked');
    const container = wrapper.closest('[data-dq-pick]');
    const input = document.getElementById(label.htmlFor);
    wrapper.classList.remove('dq-unpicked');
    const wasChecked = input?.checked;
    setTimeout(() => {
      if (wasChecked) input.dispatchEvent(new Event('change', { bubbles: true }));
      else if (container) applyPick(container);
    });
  });

  // After the theme's own change handler (on the input) has run.
  document.addEventListener('change', (event) => {
    const container = event.target.closest?.('[data-dq-pick]');
    if (container && event.target.matches('.productOption')) applyPick(container);
  });

  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      const container =
        form.closest?.('[data-dq-pick]') ||
        (form.id && document.querySelector(`[data-dq-pick] [form="${CSS.escape(form.id)}"]`)?.closest('[data-dq-pick]'));
      if (container && container.hasAttribute('data-dq-locked')) {
        event.preventDefault();
        event.stopImmediatePropagation();
      }
    },
    true
  );

  // Another colour on the product page keeps the chosen size (and heel):
  // the link opens the variant of that colour with the same values.
  document.addEventListener('click', async (event) => {
    const tile = event.target.closest?.('.dq-swatches--product a.dq-swatches__tile');
    if (!tile || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey) return;
    const container = tile.closest('[data-product-content]');
    if (!container) return;

    const chosen = {};
    container.querySelectorAll('[data-product-variants] .product-variants-wrapper:not(.dq-unpicked)').forEach((wrapper) => {
      const checked = wrapper.querySelector('input.productOption:checked');
      if (checked) chosen[optionName(wrapper).toLowerCase()] = checked.value;
    });
    if (!Object.keys(chosen).length) return;

    // Values not picked yet (heel after the size) stay open on the next page.
    const open = [...container.querySelectorAll('.dq-unpicked')].map((wrapper) => optionName(wrapper).toLowerCase());
    try {
      sessionStorage.setItem('dqUnpicked', JSON.stringify(open));
    } catch (error) {}

    event.preventDefault();
    const url = tile.href.split('?')[0];
    try {
      const product = await (await fetch(`${url}.js`)).json();
      const names = product.options.map((option) => String(option.name || option).toLowerCase());
      const fits = (keys) =>
        product.variants.filter((variant) =>
          names.every((name, index) => !keys.includes(name) || variant.options[index] === chosen[name])
        );
      let matches = fits(Object.keys(chosen));
      if (!matches.length) matches = fits(Object.keys(chosen).filter((name) => PICK.test(name) && !HEEL.test(name)));
      const variant = matches.find((match) => match.available) || matches[0];
      window.location.href = variant ? `${url}?variant=${variant.id}` : url;
    } catch (error) {
      window.location.href = url;
    }
  });

  const setupAllPicks = () => document.querySelectorAll('[data-product-content]').forEach(setupPick);

  window.addEventListener('resize', updateAll);
  // "Load more" shows cards that are already in the page, which the observer
  // below does not see.
  document.addEventListener('click', () => setTimeout(updateAll, 400));
  window.addEventListener('load', updateAll);
  new MutationObserver(updateAll).observe(document.documentElement, { childList: true, subtree: true });
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', updateAll);
  } else {
    updateAll();
  }
})();
